import type { FunctionComponent } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useParams, useLocation } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isLogined, $isSubAccount } from "../../store/user_info.ts";
import { useStore } from "@nanostores/preact";
import { gotoLoginPage, navigate } from "../../router_utils.ts";
import { requestDelete, requestGet, requestPost, requestWithParams } from "../../utils/request.ts";
import { useConfirm } from "../../components/dialog/dialog.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import { updateCameraInfo, $cameraList } from "../../store/cameras.ts";

type OSSInfo = {
    oss_id: number;
    oss_name: string;
    paid: boolean;
};

type GetOSSListResponse = {
    osses: OSSInfo[];
};

type CameraDetailResponse = {
    camera_id: number;
    camera_name: string;
    group_name?: string;
    oss_id: number | null;
    camera_key?: string;
};

export const CameraManagerPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const confirm = useConfirm();
    const params = useParams();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const cameraList = useStore($cameraList);
    const [location, _setLocation] = useLocation();

    const idParam = params["id"] ?? "new";
    const isCreate = idParam === "new";
    const cameraId = isCreate ? -1 : Number.parseInt(idParam);

    const [isBusy, setIsBusy] = useState(false);
    const [cameraName, setCameraName] = useState("");
    const [groupName, setGroupName] = useState("");
    const [ossId, setOssId] = useState<number | null>(null);
    const [ossList, setOssList] = useState([] as OSSInfo[]);

    // Compute existing groups for autocomplete
    const existingGroups = useMemo(() => {
        const groups = new Set<string>();
        cameraList.forEach(c => {
            if (c.group_name && c.group_name.trim()) {
                groups.add(c.group_name.trim());
            }
        });
        return Array.from(groups).sort();
    }, [cameraList]);

    useEffect(() => {
        document.title = isCreate ? t({ zh: "新增相机", en: "New Camera" }) : t({ zh: "相机管理", en: "Camera" });
    }, [t, isCreate]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? (isCreate ? "/camera/new" : `/camera/${cameraId}`));
        } else if (isSubAccount) {
            toast(t({ zh: "子账户无权管理相机", en: "Access Denied" }), "error");
            navigate("/cameras", { replace: true });
        }
    }, [isLogined, location, isCreate, cameraId, isSubAccount, toast, t]);

    const load = useMemo(() => async () => {
        if (!isLogined || isSubAccount) return;
        try {
            setIsBusy(true);
            const [ossResp, camResp] = await Promise.all([
                requestGet<GetOSSListResponse>("/v2/oss"),
                isCreate ? Promise.resolve(null) : requestGet<CameraDetailResponse>("/v2/camera_detail", { camera_id: cameraId }),
            ]);
            setOssList(ossResp.osses ?? []);
            if (camResp) {
                setCameraName(camResp.camera_name ?? "");
                setGroupName(camResp.group_name ?? "");
                setOssId(camResp.oss_id ?? null);
            } else {
                setCameraName("");
                setGroupName("");
                setOssId(null);
            }
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [isLogined, isCreate, cameraId, toast]);

    useEffect(() => {
        load();
    }, [load]);

    const saveAction = useMemo(() => async () => {
        if (cameraName.trim().length <= 0) {
            toast(t({ zh: "请输入相机名称", en: "Please input camera name" }), "warn");
            return;
        }
        try {
            setIsBusy(true);
            if (isCreate) {
                const resp = await requestPost<CameraDetailResponse>("/v2/camera", {
                    camera_name: cameraName.trim(),
                    group_name: groupName.trim(),
                    oss_id: ossId,
                });
                await updateCameraInfo();
                toast(t({ zh: "新增成功", en: "Created" }), "success");
                navigate(`/camera/${resp.camera_id}`, { replace: true });
            } else {
                await requestWithParams("PATCH", "/v2/camera", {
                    camera_id: cameraId,
                    camera_name: cameraName.trim(),
                    group_name: groupName.trim(),
                    oss_id: ossId,
                });
                await updateCameraInfo();
                toast(t({ zh: "保存成功", en: "Saved" }), "success");
            }
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [cameraName, groupName, ossId, isCreate, cameraId, toast, t]);

    const deleteAction = useMemo(() => async () => {
        const ok = await confirm(t({ zh: "确认删除该相机？此操作不可恢复。", en: "Delete this camera? This cannot be undone." }));
        if (!ok) return;
        try {
            setIsBusy(true);
            await requestDelete("/v2/camera", { camera_id: cameraId });
            await updateCameraInfo();
            toast(t({ zh: "删除成功", en: "Deleted" }), "success");
            navigate("/cameras", { replace: true });
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [cameraId, confirm, toast, t]);

    const resetKeyAction = useMemo(() => async () => {
        try {
            setIsBusy(true);
            const resp = await requestPost<CameraDetailResponse>("/v2/camera/reset_key", { camera_id: cameraId });
            toast(t({ zh: `相机ID: ${resp.camera_id}, 密钥: ${resp.camera_key ?? ""}`, en: `Camera ID: ${resp.camera_id}, Key: ${resp.camera_key ?? ""}` }), "normal", "long");
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [cameraId, toast, t]);

    if (!isLogined) {
        return (
            <div>
                <article aria-busy="true" class="container"></article>
            </div>
        );
    }

    return (
        <div>
            <div class="farpov_top_container">
                <form onSubmit={(e) => e.preventDefault()}>
                    <label>
                        {t({ zh: "相机名称", en: "Camera Name" })}
                        <input
                            type="text"
                            value={cameraName}
                            onInput={(e) => setCameraName((e.target as HTMLInputElement).value)}
                            disabled={isBusy}
                        />
                    </label>
                    <label>
                        {t({ zh: "分组名称", en: "Group Name" })}
                        <input
                            type="text"
                            value={groupName}
                            onInput={(e) => setGroupName((e.target as HTMLInputElement).value)}
                            disabled={isBusy}
                            placeholder={t({ zh: "选填，用于分组显示", en: "Optional, for grouping" })}
                            list="group-list"
                        />
                        <datalist id="group-list">
                            {existingGroups.map(g => <option value={g} key={g} />)}
                        </datalist>
                    </label>
                    <label>
                        {t({ zh: "绑定存储（可选）", en: "Bind Storage (optional)" })}
                        <select
                            value={ossId === null ? "" : ossId.toString()}
                            onChange={(e) => {
                                const v = (e.target as HTMLSelectElement).value;
                                setOssId(v === "" ? null : Number.parseInt(v));
                            }}
                            disabled={isBusy}
                        >
                            <option value="">{t({ zh: "不绑定", en: "None" })}</option>
                            {ossList.map((oss) => (
                                <option key={oss.oss_id} value={oss.oss_id.toString()}>
                                    {oss.oss_name}{oss.paid ? t({ zh: "（付费）", en: " (paid)" }) : ""}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div class="grid">
                        <button type="submit" aria-busy={isBusy} disabled={isBusy} onClick={saveAction}>
                            {t({ zh: "保存", en: "Save" })}
                        </button>
                        {isCreate ? (
                            <button type="button" class="secondary" disabled={isBusy} onClick={() => navigate("/cameras", { replace: false })}>
                                {t({ zh: "返回", en: "Back" })}
                            </button>
                        ) : (
                            <button type="button" class="secondary" disabled={isBusy} onClick={() => navigate(`/photos/${cameraId}`, { replace: false })}>
                                {t({ zh: "查看照片", en: "Pic" })}
                            </button>
                        )}
                    </div>
                    {!isCreate ? (
                        <>
                            <hr />
                            <div class="grid">
                                <button type="button" class="secondary" disabled={isBusy} onClick={() => navigate(`/camera_setting/${cameraId}`, { replace: false })}>
                                    {t({ zh: "相机设置", en: "Camera Settings" })}
                                </button>
                                <button type="button" class="secondary" disabled={isBusy} onClick={resetKeyAction}>
                                    {t({ zh: "显示密钥", en: "Show Key" })}
                                </button>
                                <button type="button" class="contrast" aria-busy={isBusy} disabled={isBusy} onClick={deleteAction}>
                                    {t({ zh: "删除相机", en: "Delete" })}
                                </button>
                            </div>
                        </>
                    ) : null}
                </form>
            </div>
        </div>
    );
};

export default CameraManagerPage;
